const KEY_BP_XP = 'fps_bp_xp_v2';          // bumped to v2 for 100-tier scale
const KEY_BP_CLAIMED = 'fps_bp_claimed_v2'; // bumped to v2 for expanded tiers
const KEY_BP_REROLLS = 'fps_bp_rerolls';    // {date, left}
const KEY_LOGIN_STREAK = 'fps_bp_login_streak_v1'; // {lastDate, count, claimedDate}

export const BP_TIER_COUNT = 100;
export const BP_XP_PER_TIER = 100;

export type BpRewardType = 'outfit' | 'trail' | 'title';

export type BpTierReward = {
  label: string;
  type: BpRewardType;
  /** Outfit swatch index in `OUTFIT_SWATCH_HUES` (outfit tiers). */
  swatchIndex?: number;
};

export const TIER_REWARDS: Record<number, BpTierReward> = {
  // Original tiers (kept intact)
  2:  { label: 'Azure Outfit',       type: 'outfit', swatchIndex: 2 },
  4:  { label: 'Jade Outfit',        type: 'outfit', swatchIndex: 3 },
  6:  { label: 'Prism Glide Trail',  type: 'trail',  swatchIndex: 4 },
  8:  { label: 'Sunset Outfit',      type: 'outfit', swatchIndex: 5 },
  10: { label: 'Storm Surge',        type: 'title' },
  // Expanded tiers
  20: { label: 'Neon Viper Outfit',   type: 'outfit', swatchIndex: 6 },
  30: { label: 'Shadow Blaze Trail',  type: 'trail' },
  40: { label: 'Crimson Phantom Outfit', type: 'outfit', swatchIndex: 7 },
  50: { label: 'Eye of the Storm',    type: 'title' },
  60: { label: 'Void Walker Outfit',  type: 'outfit', swatchIndex: 8 },
  70: { label: 'Aurora Drift Trail',  type: 'trail' },
  80: { label: 'Tempest Outfit',      type: 'outfit', swatchIndex: 9 },
  90: { label: 'Storm Rider',         type: 'title' },
  100:{ label: 'Season Legend Outfit',type: 'outfit', swatchIndex: 10 },
};

const REWARD_TIERS = [2, 4, 6, 8, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100] as const;
type RewardTier = (typeof REWARD_TIERS)[number];

// ── Weekly quest seeds (5 per week, derived from ISO week number) ──────────
const WEEKLY_QUEST_POOL: string[][] = [
  ['Deal 500 damage',          'Outlast 20 players',       'Open 5 chests',          'Win a match',            'Reach storm phase 5'],
  ['Eliminate 5 opponents',    'Deal 1000 damage',         'Survive 3 matches',      'Outlast 50 players',     'Open 10 chests'],
  ['Win 2 matches',            'Deal 750 damage',          'Reach phase 4 alive',    'Eliminate 3 opponents',  'Loot 8 chests'],
  ['Outlast 30 players',       'Eliminate 7 opponents',    'Win without taking dmg', 'Open 6 chests',          'Deal 600 damage'],
  ['Reach top 5 in a match',   'Deal 1500 damage',         'Win 3 matches',          'Outlast 100 players',    'Eliminate 10 opponents'],
  ['Open 12 chests',           'Deal 800 damage',          'Outlast 40 players',     'Win with only pistol',   'Eliminate 4 opponents'],
  ['Deal 2000 damage',         'Win 5 matches',            'Outlast 200 players',    'Open 20 chests',         'Eliminate 15 opponents'],
];

// ── localStorage helpers ───────────────────────────────────────────────────

function todayKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function isoWeekNumber(): number {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const week1 = new Date(d.getFullYear(), 0, 4);
  return 1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
}

function readInt(key: string): number {
  try {
    const raw = localStorage.getItem(key);
    const n = raw ? parseInt(raw, 10) : 0;
    return Number.isFinite(n) && n >= 0 ? n : 0;
  } catch {
    return 0;
  }
}

function writeInt(key: string, value: number): void {
  try {
    localStorage.setItem(key, String(Math.max(0, Math.floor(value))));
  } catch {
    /* ignore quota / private mode */
  }
}

function readClaimedTiers(): Set<number> {
  try {
    const raw = localStorage.getItem(KEY_BP_CLAIMED);
    if (!raw) return new Set();
    const parts = raw.split(',').map((s) => parseInt(s.trim(), 10));
    return new Set(parts.filter((n) => Number.isFinite(n) && (REWARD_TIERS as readonly number[]).includes(n)));
  } catch {
    return new Set();
  }
}

function writeClaimedTiers(claimed: Set<number>): void {
  try {
    const sorted = [...claimed].sort((a, b) => a - b);
    localStorage.setItem(KEY_BP_CLAIMED, sorted.join(','));
  } catch {
    /* ignore quota / private mode */
  }
}

// ── BP animation styles ────────────────────────────────────────────────────

function ensureBpAnimStyles(): void {
  if (document.getElementById('bp-tier-up-style')) return;
  const style = document.createElement('style');
  style.id = 'bp-tier-up-style';
  style.textContent = `
    @keyframes bpTierUp {
      0%   { opacity:0; transform:translate(-50%,-60%) scale(0.85); }
      15%  { opacity:1; transform:translate(-50%,-60%) scale(1.04); }
      70%  { opacity:1; transform:translate(-50%,-60%) scale(1); }
      100% { opacity:0; transform:translate(-50%,-70%) scale(0.95); }
    }
    @keyframes bpTierUpFull {
      0%   { opacity:0; }
      8%   { opacity:1; }
      82%  { opacity:1; }
      100% { opacity:0; }
    }
    @keyframes bpTierUpBurst {
      0%   { opacity:0; transform:scale(0.6); }
      18%  { opacity:1; transform:scale(1.08); }
      72%  { opacity:1; transform:scale(1); }
      100% { opacity:0; transform:scale(1.12); }
    }
    @keyframes bpElimXpFloat {
      0%   { opacity:0; transform:translateY(12px) scale(0.9); }
      12%  { opacity:1; transform:translateY(0) scale(1.05); }
      75%  { opacity:1; transform:translateY(-28px) scale(1); }
      100% { opacity:0; transform:translateY(-52px) scale(0.95); }
    }
  `;
  document.head.appendChild(style);
}

/** Full-screen tier-up celebration (phase 25). */
function showTierUpCelebration(tier: number, reward?: BpTierReward): void {
  ensureBpAnimStyles();

  const backdrop = document.createElement('div');
  backdrop.style.cssText = [
    'position:fixed',
    'inset:0',
    'z-index:7999',
    'pointer-events:none',
    'background:radial-gradient(ellipse at center,rgba(255,200,50,0.22) 0%,rgba(6,10,18,0.88) 68%)',
    'animation:bpTierUpFull 3.2s ease forwards',
  ].join(';');

  const card = document.createElement('div');
  card.style.cssText = [
    'position:fixed',
    'top:50%',
    'left:50%',
    'transform:translate(-50%,-50%)',
    'z-index:8000',
    'pointer-events:none',
    'text-align:center',
    'user-select:none',
    'animation:bpTierUpBurst 3s ease forwards',
  ].join(';');

  const badge = document.createElement('div');
  badge.style.cssText = [
    'display:inline-block',
    'padding:18px 36px 22px',
    'border-radius:18px',
    'background:linear-gradient(165deg,rgba(36,28,8,0.96),rgba(12,10,6,0.98))',
    'border:3px solid rgba(255,213,74,0.85)',
    'box-shadow:0 0 48px rgba(255,200,50,0.45),0 18px 48px rgba(0,0,0,0.55)',
  ].join(';');

  const headline = document.createElement('div');
  headline.style.cssText = [
    'font:900 clamp(28px,6vw,52px) system-ui,sans-serif',
    'color:#ffd54a',
    'text-shadow:0 0 32px rgba(255,200,50,0.95),0 3px 10px rgba(0,0,0,0.85)',
    'letter-spacing:0.1em',
    'text-transform:uppercase',
  ].join(';');
  headline.textContent = 'Tier Up!';

  const tierLabel = document.createElement('div');
  tierLabel.style.cssText = [
    'margin-top:8px',
    'font:900 clamp(36px,8vw,72px) system-ui,sans-serif',
    'color:#fff8e8',
    'text-shadow:0 0 24px rgba(255,230,140,0.75)',
    'letter-spacing:0.04em',
  ].join(';');
  tierLabel.textContent = `Tier ${tier}`;

  badge.appendChild(headline);
  badge.appendChild(tierLabel);

  if (reward) {
    const rewardLabel = document.createElement('div');
    rewardLabel.style.cssText = [
      'margin-top:14px',
      'font:700 clamp(16px,3vw,24px) system-ui,sans-serif',
      'color:#ffe9a0',
      'letter-spacing:0.04em',
    ].join(';');
    rewardLabel.textContent = `Unlocked: ${reward.label}`;
    badge.appendChild(rewardLabel);
  }

  card.appendChild(badge);
  document.body.appendChild(backdrop);
  document.body.appendChild(card);
  setTimeout(() => {
    backdrop.remove();
    card.remove();
  }, 3200);
}

function showLoginStreakPopup(streak: number, rewardXp: number): void {
  ensureBpAnimStyles();
  const overlay = document.createElement('div');
  overlay.style.cssText = [
    'position:fixed',
    'top:50%',
    'left:50%',
    'transform:translate(-50%,-60%)',
    'z-index:8100',
    'pointer-events:none',
    'text-align:center',
    'user-select:none',
    'animation:bpTierUp 2.8s ease forwards',
  ].join(';');

  const streakLabel = document.createElement('div');
  streakLabel.style.cssText = [
    'font:900 36px system-ui,sans-serif',
    'color:#7ef9ff',
    'text-shadow:0 0 24px rgba(100,240,255,0.85),0 2px 6px rgba(0,0,0,0.8)',
    'letter-spacing:0.06em',
    'white-space:nowrap',
  ].join(';');
  streakLabel.textContent = `${streak}-Day Login Streak!`;

  const rewardLabel = document.createElement('div');
  rewardLabel.style.cssText = [
    'margin-top:10px',
    'font:700 20px system-ui,sans-serif',
    'color:#ffe9a0',
    'text-shadow:0 1px 6px rgba(0,0,0,0.9)',
    'letter-spacing:0.04em',
  ].join(';');
  rewardLabel.textContent = `+${rewardXp} Battle Pass XP`;

  overlay.appendChild(streakLabel);
  overlay.appendChild(rewardLabel);
  document.body.appendChild(overlay);
  setTimeout(() => overlay.remove(), 2900);
}

// ── Main class ─────────────────────────────────────────────────────────────

/** Persistent Season battle pass XP (localStorage `fps_bp_xp_v2`). */
export class FpsBattlePass {
  // ── Core XP / Tier ──────────────────────────────────────────────────────

  static loadXp(): number {
    return readInt(KEY_BP_XP);
  }

  static saveXp(xp: number): void {
    writeInt(KEY_BP_XP, xp);
  }

  static getTier(xp: number = this.loadXp()): number {
    const tier = Math.floor(xp / BP_XP_PER_TIER) + 1;
    return Math.min(BP_TIER_COUNT, Math.max(1, tier));
  }

  static getProgressInTier(xp: number = this.loadXp()): number {
    const capped = Math.min(xp, BP_TIER_COUNT * BP_XP_PER_TIER);
    return capped % BP_XP_PER_TIER;
  }

  /**
   * Returns 0–1 progress within the current tier.
   * 1.0 means the player is at max tier (tier 100).
   */
  static getProgressPct(xp: number = this.loadXp()): number {
    const tier = this.getTier(xp);
    if (tier >= BP_TIER_COUNT) return 1;
    return this.getProgressInTier(xp) / BP_XP_PER_TIER;
  }

  static addXp(amount: number): { xp: number; tier: number; progress: number; prevTier: number } {
    const prevTier = this.getTier();
    const next = this.loadXp() + Math.max(0, Math.floor(amount));
    this.saveXp(next);
    const tier = this.getTier(next);

    // Full-screen celebration for every tier gained
    if (tier > prevTier) {
      const reward = TIER_REWARDS[tier as RewardTier];
      showTierUpCelebration(tier, reward);
    }

    return {
      xp: next,
      tier,
      progress: this.getProgressInTier(next),
      prevTier,
    };
  }

  // ── Reward claiming ──────────────────────────────────────────────────────

  static isTierClaimed(tier: number): boolean {
    return readClaimedTiers().has(tier);
  }

  /** Claim every unclaimed reward tier up to `tier`; returns newly claimed rewards. */
  static claimPendingRewards(tier: number): BpTierReward[] {
    const claimed = readClaimedTiers();
    const fresh: BpTierReward[] = [];
    for (const t of REWARD_TIERS) {
      if (t > tier || claimed.has(t)) continue;
      const reward = TIER_REWARDS[t];
      if (!reward) continue;
      claimed.add(t);
      fresh.push(reward);
    }
    if (fresh.length > 0) writeClaimedTiers(claimed);
    return fresh;
  }

  static getNextRewardPreview(): string {
    const tier = this.getTier();
    const xp = this.loadXp();
    const atMax = tier >= BP_TIER_COUNT && xp >= BP_TIER_COUNT * BP_XP_PER_TIER;
    if (atMax) return 'All rewards unlocked';

    for (const t of REWARD_TIERS) {
      if (t > tier) {
        const reward = TIER_REWARDS[t]!;
        return `Next unlock · Tier ${t}: ${reward.label}`;
      }
    }
    return 'All rewards unlocked';
  }

  // ── Swatch / trail / title unlocks ──────────────────────────────────────

  /** Swatch indices 0–1 are free; higher indices unlock via outfit BP tiers. */
  static isSwatchUnlocked(swatchIndex: number, tier: number = this.getTier()): boolean {
    if (swatchIndex <= 1) return true;
    for (const t of REWARD_TIERS) {
      const reward = TIER_REWARDS[t];
      if (!reward?.swatchIndex || reward.swatchIndex !== swatchIndex) continue;
      return tier >= t;
    }
    return false;
  }

  static isTrailUnlocked(tier: number = this.getTier()): boolean {
    return tier >= 6;
  }

  static isTitleUnlocked(tier: number = this.getTier()): boolean {
    return tier >= 10;
  }

  static getSwatchUnlockTier(swatchIndex: number): number | null {
    for (const t of REWARD_TIERS) {
      const reward = TIER_REWARDS[t];
      if (reward?.swatchIndex === swatchIndex) return t;
    }
    return null;
  }

  // ── XP helpers ───────────────────────────────────────────────────────────

  /** Match-end grant: elims×10 + (win ? 100 : 20). */
  static xpForMatch(elims: number, won: boolean): number {
    return Math.max(0, elims) * 10 + (won ? 100 : 20);
  }

  /** Per-elimination BP XP burst amount (phase 23). */
  static xpForElim(headshot = false): number {
    return headshot ? 15 : 10;
  }

  /** Floating +XP text on elimination (phase 23). */
  static showElimXpBurst(amount: number): void {
    if (amount <= 0) return;
    ensureBpAnimStyles();
    const el = document.createElement('div');
    el.style.cssText = [
      'position:fixed',
      'right:18%',
      'top:42%',
      'z-index:8050',
      'pointer-events:none',
      'user-select:none',
      'font:900 26px system-ui,sans-serif',
      'color:#7ef9ff',
      'text-shadow:0 0 18px rgba(100,240,255,0.85),0 2px 8px rgba(0,0,0,0.75)',
      'letter-spacing:0.06em',
      'animation:bpElimXpFloat 1.35s ease forwards',
    ].join(';');
    el.textContent = `+${amount} XP`;
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 1400);
  }

  static getRewardTiers(): readonly number[] {
    return REWARD_TIERS;
  }

  // ── Daily login streak ───────────────────────────────────────────────────

  private static _readLoginStreak(): { lastDate: string; count: number; claimedDate: string } {
    try {
      const raw = localStorage.getItem(KEY_LOGIN_STREAK);
      if (!raw) return { lastDate: '', count: 0, claimedDate: '' };
      const parsed = JSON.parse(raw) as { lastDate?: string; count?: number; claimedDate?: string };
      const count = Number.isFinite(parsed.count) ? Math.max(0, Math.floor(parsed.count!)) : 0;
      return {
        lastDate: parsed.lastDate ?? '',
        count,
        claimedDate: parsed.claimedDate ?? '',
      };
    } catch {
      return { lastDate: '', count: 0, claimedDate: '' };
    }
  }

  private static _writeLoginStreak(data: { lastDate: string; count: number; claimedDate: string }): void {
    try {
      localStorage.setItem(KEY_LOGIN_STREAK, JSON.stringify(data));
    } catch {
      /* ignore */
    }
  }

  /** XP grant for daily login streak (caps at day 7+). */
  static loginStreakRewardXp(streak: number): number {
    const day = Math.max(1, Math.min(7, streak));
    return 25 + day * 15;
  }

  static getLoginStreak(): number {
    const data = this._readLoginStreak();
    if (data.lastDate === todayKey()) return Math.max(1, data.count);
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yKey = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, '0')}-${String(yesterday.getDate()).padStart(2, '0')}`;
    return data.lastDate === yKey ? Math.max(1, data.count) : 0;
  }

  /**
   * Process daily login on lobby load.
   * Returns reward info when today's streak was newly claimed.
   */
  static processDailyLogin(): { streak: number; rewardXp: number } | null {
    const today = todayKey();
    const data = this._readLoginStreak();
    if (data.claimedDate === today) return null;

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yKey = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, '0')}-${String(yesterday.getDate()).padStart(2, '0')}`;

    let streak = 1;
    if (data.lastDate === yKey) streak = Math.max(1, data.count) + 1;
    else if (data.lastDate === today) streak = Math.max(1, data.count);

    const rewardXp = this.loginStreakRewardXp(streak);
    this._writeLoginStreak({ lastDate: today, count: streak, claimedDate: today });
    this.addXp(rewardXp);
    showLoginStreakPopup(streak, rewardXp);
    return { streak, rewardXp };
  }

  // ── Daily quest rerolls ──────────────────────────────────────────────────

  private static _readRerolls(): { date: string; left: number } {
    try {
      const raw = localStorage.getItem(KEY_BP_REROLLS);
      if (!raw) return { date: todayKey(), left: 3 };
      const parsed = JSON.parse(raw) as { date: string; left: number };
      if (parsed.date !== todayKey()) return { date: todayKey(), left: 3 };
      const left = Number.isFinite(parsed.left) ? Math.max(0, Math.min(3, parsed.left)) : 3;
      return { date: parsed.date, left };
    } catch {
      return { date: todayKey(), left: 3 };
    }
  }

  private static _writeRerolls(data: { date: string; left: number }): void {
    try {
      localStorage.setItem(KEY_BP_REROLLS, JSON.stringify(data));
    } catch {
      /* ignore */
    }
  }

  /** How many daily quest rerolls remain today (resets at midnight, max 3). */
  static getDailyRerollsLeft(): number {
    return this._readRerolls().left;
  }

  /**
   * Consume one reroll for the day.
   * Returns `true` if a reroll was available and consumed; `false` otherwise.
   * The caller is responsible for picking the new quest from `FpsDailyChallenges`.
   */
  static rerollDailyQuest(): boolean {
    const data = this._readRerolls();
    if (data.left <= 0) return false;
    data.left -= 1;
    this._writeRerolls(data);
    return true;
  }

  // ── Weekly quests ────────────────────────────────────────────────────────

  /**
   * Returns 5 weekly quest names for the current ISO week.
   * Rotates through `WEEKLY_QUEST_POOL` by week number.
   */
  static getWeeklyQuestNames(): string[] {
    const weekIdx = isoWeekNumber() % WEEKLY_QUEST_POOL.length;
    return [...WEEKLY_QUEST_POOL[weekIdx]];
  }

  // ── Season metadata ──────────────────────────────────────────────────────

  /** Human-readable season label shown in lobby / battle pass screen. */
  static getSeasonDescription(): string {
    return 'Chapter 1, Season 1';
  }

  // ── UI helpers ───────────────────────────────────────────────────────────

  /** HTML chips for lobby — claimed vs locked reward tiers. */
  static getClaimedGalleryHtml(): string {
    const tier = this.getTier();
    const claimed = readClaimedTiers();
    const chips = REWARD_TIERS.map((t) => {
      const reward = TIER_REWARDS[t]!;
      const unlocked = tier >= t;
      const done = claimed.has(t);
      const bg = done ? 'rgba(255,213,74,0.35)' : unlocked ? 'rgba(120,180,255,0.22)' : 'rgba(255,255,255,0.06)';
      const border = done ? 'rgba(255,213,74,0.75)' : unlocked ? 'rgba(120,180,255,0.45)' : 'rgba(255,255,255,0.12)';
      const opacity = unlocked ? '1' : '0.45';
      return (
        `<span title="${reward.label}" style="display:inline-flex;align-items:center;justify-content:center;` +
        `min-width:28px;height:22px;padding:0 6px;border-radius:6px;font:700 10px system-ui;` +
        `color:#fff;background:${bg};border:1px solid ${border};opacity:${opacity}">T${t}</span>`
      );
    }).join('');
    return `<div style="display:flex;flex-wrap:wrap;gap:6px;margin-top:8px">${chips}</div>`;
  }
}
